import { Injectable, Injector } from '@angular/core';
import { limit, query, where, QueryConstraint } from 'firebase/firestore';
import { AuthService } from '@auth';
import { FirestoreService } from './firestore.service';
import { PreferredOrganizationService } from './preferred-organization.service';
import { ModuleActivationService } from './module-activation.service';
import { ReplicationSyncStatus } from '@shared/models/replication-sync-status';
import { CashRegisterStatus } from '@shared/models/cash-register';
import {
  PreferredOrgSyncState,
  PreferredOrgUserPendingState,
  UnsyncedEntityKind,
  UnsyncedSummary
} from '@shared/models/preferred-organization';

const SALES_ORDERS_V3_COLLECTION = 'salesOrdersV3';
// Mirrors SALES_ORDERS_V25_COLLECTION in sales-order-replication.service. Kept as a
// literal: importing that service here would drag the whole replication graph into
// this file's module closure, and this file is loaded very early (route guards).
const SALES_ORDERS_V25_COLLECTION = 'salesOrdersV2.5';
const CASH_TRANSACTIONS_COLLECTION = 'cashTransactions';
const CASH_REGISTER_STATUS_LOGS_COLLECTION = 'cashRegisterStatusLogs';
const CASH_REGISTERS_COLLECTION = 'cashRegisters';

/** Anything that still has to reach the backend counts as pending. */
const PENDING_STATUSES = [
  ReplicationSyncStatus.UnSynced,
  ReplicationSyncStatus.Syncing,
  ReplicationSyncStatus.Failed
];

const QUERY_LIMIT = 500;

/**
 * Aggregates the preferred organization's pending (unsynced) local records across
 * entities (US 27702): Firestore sales orders (v3 or v2.5 per module activation),
 * cash transactions, register closure logs, open registers, and Dexie offline
 * customers. It gates changing the preferred organization and maintains the
 * persisted browser-level sync-state flag that survives user changes.
 *
 * Data-access policy: a normal user's detail view covers only records they created;
 * a SystemAdministrator sees every user's. The change gate always uses the full
 * aggregate — other users' counts come from the persisted flag when tenant-wide
 * queries are not permitted.
 */
@Injectable({
  providedIn: 'root'
})
export class UnsyncedRecordsService {

  public static readonly SYNC_STATE_KEY = 'edara3_preferred_org_sync_state';

  constructor(
    private authService: AuthService,
    private firestoreService: FirestoreService,
    private preferredOrganizationService: PreferredOrganizationService,
    private injector: Injector,
    private moduleActivationService: ModuleActivationService
  ) { }

  //#region live queries (meaningful while the preferred tenant's data is in this browser)

  /** Pending counts for the records the SIGNED-IN user created, plus browser-level items. */
  public async getOwnSummaryAsync(): Promise<UnsyncedSummary> {
    return this.buildSummaryAsync(this.authService.currentUserEmail);
  }

  /**
   * The gate for changing the preferred organization: pending counts across ALL users.
   * Tries tenant-wide queries first; where security rules deny them, falls back to the
   * signed-in user's live counts merged with other users' persisted flag entries.
   */
  public async getGateSummaryAsync(): Promise<UnsyncedSummary> {
    try {
      return await this.buildSummaryAsync(undefined);
    } catch {
      try {
        const own = await this.getOwnSummaryAsync();
        return this.mergeWithPersistedOtherUsers(own);
      } catch {
        // No live query possible at all: the persisted flag is the last word.
        return this.summarizePersistedState(this.readPersistedState());
      }
    }
  }

  /** Preview rows for one entity kind, honoring the data-access policy via `allUsers`. */
  public async getPendingRecordsAsync(kind: UnsyncedEntityKind, allUsers: boolean, maxRecords = 50): Promise<any[]> {
    const email = allUsers ? undefined : this.authService.currentUserEmail;
    const collectionName = kind === 'salesOrders'
      ? await this.salesOrdersCollectionAsync()
      : kind === 'cashTransactions' ? CASH_TRANSACTIONS_COLLECTION : CASH_REGISTER_STATUS_LOGS_COLLECTION;
    return this.queryPendingAsync<any>(collectionName, email, maxRecords);
  }

  private async buildSummaryAsync(email: string | undefined): Promise<UnsyncedSummary> {
    const tenantId = this.tenantIdForQueries();
    const [salesOrders, cashTransactions, closures, openRegisters, offlineCustomers] = await Promise.all([
      this.countPendingAsync(await this.salesOrdersCollectionAsync(), tenantId, email),
      this.countPendingAsync(CASH_TRANSACTIONS_COLLECTION, tenantId, email),
      this.countPendingAsync(CASH_REGISTER_STATUS_LOGS_COLLECTION, tenantId, email),
      this.countOpenRegistersAsync(tenantId),
      this.countOfflineCustomersAsync()
    ]);

    return {
      salesOrders,
      cashTransactions,
      closures,
      offlineCustomers,
      openRegisters,
      total: salesOrders + cashTransactions + closures + offlineCustomers + openRegisters
    };
  }

  /** Queries always target the preferred tenant's records (that is whose data is local). */
  private tenantIdForQueries(): string {
    return this.preferredOrganizationService.preferred?.tenantId ?? this.authService.currentTenant;
  }

  private async salesOrdersCollectionAsync(): Promise<string> {
    const isV3 = await this.moduleActivationService.isPointOfSaleV3Enabled();
    return isV3 ? SALES_ORDERS_V3_COLLECTION : SALES_ORDERS_V25_COLLECTION;
  }

  private async countPendingAsync(collectionName: string, tenantId: string, email: string | undefined): Promise<number> {
    const records = await this.queryPendingByTenantAsync<any>(collectionName, tenantId, email, QUERY_LIMIT);
    return records.length;
  }

  private async queryPendingAsync<T>(collectionName: string, email: string | undefined, maxRecords: number): Promise<T[]> {
    return this.queryPendingByTenantAsync<T>(collectionName, this.tenantIdForQueries(), email, maxRecords);
  }

  private async queryPendingByTenantAsync<T>(
    collectionName: string,
    tenantId: string,
    email: string | undefined,
    maxRecords: number
  ): Promise<T[]> {
    // One equality query per status (mirroring the push services) so Firestore can
    // serve them from single-field indexes without composite-index deployments.
    const resultsPerStatus = await Promise.all(PENDING_STATUSES.map(status => {
      const constraints: QueryConstraint[] = [
        where('metadata.organizationInfo.id', '==', tenantId),
        ...(email ? [where('metadata.createdBy.email', '==', email)] : []),
        where('metadata.syncInfo.syncStatus', '==', status),
        limit(maxRecords)
      ];
      return this.firestoreService.getCollectionAsync<T>(
        query(this.firestoreService.col<T>(collectionName), ...constraints)
      );
    }));
    return resultsPerStatus.flat().slice(0, maxRecords);
  }

  private async countOpenRegistersAsync(tenantId: string): Promise<number> {
    const queryRef = query(
      this.firestoreService.col<any>(CASH_REGISTERS_COLLECTION),
      where('metadata.organizationInfo.id', '==', tenantId),
      // The documents store the numeric enum (CashRegisterStatus.Open = 1), not a string.
      where('status', '==', CashRegisterStatus.Open),
      limit(QUERY_LIMIT)
    );
    const open = await this.firestoreService.getCollectionAsync<any>(queryRef);
    return open.length;
  }

  private async countOfflineCustomersAsync(): Promise<number> {
    try {
      // Dynamic import: CorePosService's module closure is enormous, and pulling it
      // in eagerly from here (loaded by route guards) re-creates the circular
      // evaluation that left component providers undefined at bootstrap.
      const { CorePosService } = await import('./core-pos.service');
      const customers = await this.injector.get(CorePosService).getUnsyncedCustomersV3Async();
      return customers?.length ?? 0;
    } catch {
      return 0;
    }
  }

  //#endregion

  //#region persisted sync-state flag (survives sign-out and user changes)

  public readPersistedState(): PreferredOrgSyncState | undefined {
    try {
      const raw = localStorage.getItem(UnsyncedRecordsService.SYNC_STATE_KEY);
      if (!raw) return undefined;
      const state = JSON.parse(raw) as PreferredOrgSyncState;
      if (!state?.tenantId) return undefined;

      // A flag left behind by a DIFFERENT preferred organization is meaningless and
      // must never block (or unblock) decisions about the current one.
      const preferredTenantId = this.preferredOrganizationService.preferred?.tenantId;
      if (preferredTenantId && state.tenantId !== preferredTenantId) return undefined;

      return state;
    } catch {
      return undefined;
    }
  }

  /** Sum of every user's flag entry plus the browser-level items. */
  public aggregatePersistedTotal(state: PreferredOrgSyncState | undefined): number {
    return this.summarizePersistedState(state).total;
  }

  /** Per-entity counts out of the persisted flag (every user's entries summed). */
  public summarizePersistedState(state: PreferredOrgSyncState | undefined): UnsyncedSummary {
    const entries = Object.values(state?.users ?? {});
    const salesOrders = entries.reduce((sum, entry) => sum + (entry?.salesOrders ?? 0), 0);
    const cashTransactions = entries.reduce((sum, entry) => sum + (entry?.cashTransactions ?? 0), 0);
    const closures = entries.reduce((sum, entry) => sum + (entry?.closures ?? 0), 0);
    const offlineCustomers = state?.offlineCustomers ?? 0;
    const openRegisters = state?.openRegisters ?? 0;
    return {
      salesOrders,
      cashTransactions,
      closures,
      offlineCustomers,
      openRegisters,
      total: salesOrders + cashTransactions + closures + offlineCustomers + openRegisters
    };
  }

  /**
   * Recompute the signed-in user's own entry (and the browser-level counts) and persist
   * them. Only runs while the active tenant IS the preferred one — any other session
   * has no authority over the preferred tenant's pending state.
   */
  public async refreshPersistedStateAsync(): Promise<void> {
    const preferred = this.preferredOrganizationService.preferred;
    const email = this.authService.currentUserEmail;
    if (!preferred || !email) return;
    if (!this.preferredOrganizationService.isPreferredTenant(this.authService.currentTenant)) return;

    try {
      const own = await this.getOwnSummaryAsync();
      const state: PreferredOrgSyncState = this.readPersistedState() ?? {
        tenantId: preferred.tenantId,
        updatedAt: '',
        users: {},
        offlineCustomers: 0,
        openRegisters: 0
      };

      // A flag left over from a previous preferred organization is meaningless.
      if (state.tenantId !== preferred.tenantId) {
        state.tenantId = preferred.tenantId;
        state.users = {};
      }

      const ownPending = own.salesOrders + own.cashTransactions + own.closures;
      if (ownPending > 0) {
        const entry: PreferredOrgUserPendingState = {
          salesOrders: own.salesOrders,
          cashTransactions: own.cashTransactions,
          closures: own.closures,
          total: ownPending,
          updatedAt: new Date().toISOString()
        };
        state.users[email] = entry;
      } else {
        delete state.users[email];
      }

      state.offlineCustomers = own.offlineCustomers;
      state.openRegisters = own.openRegisters;
      state.updatedAt = new Date().toISOString();

      this.writePersistedState(state);
    } catch {
      // Keeping a stale flag is safer than erasing it: the gate errs on the blocking side.
    }
  }

  /** Called by the change engine after local data is cleared. */
  public clearPersistedState(): void {
    try {
      localStorage.removeItem(UnsyncedRecordsService.SYNC_STATE_KEY);
    } catch {
      // ignore
    }
  }

  private writePersistedState(state: PreferredOrgSyncState): void {
    try {
      localStorage.setItem(UnsyncedRecordsService.SYNC_STATE_KEY, JSON.stringify(state));
    } catch {
      // Storage unavailable; the live queries remain the authority for this session.
    }
  }

  private mergeWithPersistedOtherUsers(own: UnsyncedSummary): UnsyncedSummary {
    const state = this.readPersistedState();
    const email = this.authService.currentUserEmail;
    let salesOrders = own.salesOrders;
    let cashTransactions = own.cashTransactions;
    let closures = own.closures;

    Object.entries(state?.users ?? {}).forEach(([userEmail, entry]) => {
      if (userEmail === email) return;
      salesOrders += entry?.salesOrders ?? 0;
      cashTransactions += entry?.cashTransactions ?? 0;
      closures += entry?.closures ?? 0;
    });

    const total = salesOrders + cashTransactions + closures + own.offlineCustomers + own.openRegisters;
    return { salesOrders, cashTransactions, closures, offlineCustomers: own.offlineCustomers, openRegisters: own.openRegisters, total };
  }

  //#endregion
}
